import { router } from '@inertiajs/react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowUpRight, CalendarClock, CircleX, FileText, LoaderCircle, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import MemoryDerivation from './MemoryDerivation'
import MemoryEditForm from './MemoryEditForm'
import type { MemoryCopy, MemoryDetailPayload, MemoryRow } from './types'
import { formatMemoryDate } from './utils'

interface Props {
  workerId?: string
  copy: MemoryCopy
  row: MemoryRow
  detail?: MemoryDetailPayload
  loading?: boolean
  canManage?: boolean
  onMutated?: () => void
  onSelectSource: (documentId: string) => void
  onClose: () => void
}

export default function MemoryDetailPanel({ workerId, copy, row, detail, loading = false, canManage = false, onMutated, onSelectSource, onClose }: Props) {
  const [mode, setMode] = useState<'view' | 'edit' | 'delete'>('view')
  const [processing, setProcessing] = useState(false)
  const fullMemory = detail?.status === 'ready' && detail.memory?.id === row.id ? detail.memory : null
  const text = fullMemory?.text || row.text
  // Contexto e entidades não são exibidos: o contexto vem como metadado de
  // ingestão (caminho do transcript, line=, role=, seen_at=) e as entidades
  // como taxonomia interna (memory_domain:*, source_kind:*) — ruído para quem
  // usa. O contexto continua sendo carregado porque o formulário de edição o
  // devolve intacto no PATCH; só não aparece na leitura.
  const context = fullMemory?.context ?? row.context
  // O tipo interno (world/experience/observation) não aparece mais: para quem
  // usa, tudo é memória. Ele só sobrevive aqui como regra — memória derivada
  // (observation) não pode ser editada diretamente, só invalidada.
  const rawFactType = fullMemory?.type || fullMemory?.fact_type || row.fact_type
  const derived = rawFactType === 'observation'
  const primaryDate = fullMemory?.mentioned_at || row.mentioned_at || fullMemory?.occurred_start || row.occurred_start || row.created_at
  const sources = fullMemory?.source_memories || []
  const sourceCount = fullMemory?.source_memory_count ?? sources.length
  const derivationHistory = fullMemory?.derivation_history || []
  const sourceDocumentId = fullMemory?.document_id || row.document_id
  const canInvalidate = Boolean(workerId && canManage && row.id)
  const canEdit = Boolean(canInvalidate && fullMemory && !derived)
  const memoryPath = `/dashboard/digital_workers/${workerId}/memories/${row.id}`

  const save = (nextText: string, nextContext: string) => {
    setProcessing(true)
    router.patch(memoryPath, { memory: { text: nextText, context: nextContext } }, {
      preserveScroll: true,
      onSuccess: () => {
        onMutated?.()
        onClose()
      },
      onFinish: () => setProcessing(false),
    })
  }

  const remove = () => {
    setProcessing(true)
    router.delete(memoryPath, {
      preserveScroll: true,
      onSuccess: () => {
        onMutated?.()
        onClose()
      },
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <aside className="flex h-full flex-col overflow-hidden" aria-label={copy.detail.title}>
      {/* Sem menu de três pontinhos: editar é um lápis ao lado do título (só
          quando a memória é editável) e invalidar mora no rodapé, longe do
          fluxo de leitura. */}
      <header className="flex items-center gap-2 border-b border-border px-5 py-4">
        <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">{copy.detail.title}</h3>
        {canEdit && mode === 'view' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" onClick={() => setMode('edit')} aria-label={copy.detail.edit}><Pencil /></Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>{copy.detail.edit}</TooltipContent>
          </Tooltip>
        )}
        <Button type="button" variant="ghost" size="icon-sm" onClick={onClose} aria-label={copy.detail.close}><X /></Button>
      </header>

      {mode === 'edit' ? (
        <div className="flex-1 overflow-y-auto"><MemoryEditForm copy={copy} initialText={text} initialContext={context} processing={processing} onCancel={() => setMode('view')} onSave={save} /></div>
      ) : (
        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <p className="whitespace-pre-wrap text-[15px] leading-7 text-foreground">{text}</p>

          {sourceDocumentId && (
            <DocumentLink
              label={copy.detail.view_source}
              title={copy.detail.original_document}
              description={copy.detail.original_document_description}
              onSelect={() => onSelectSource(sourceDocumentId)}
            />
          )}

          {(sources.length > 0 || derivationHistory.length > 0 || (derived && sourceCount > 0)) && (
            <MemoryDerivation
              copy={copy}
              currentText={text}
              sources={sources}
              sourceCount={sourceCount}
              truncated={Boolean(fullMemory?.source_memories_truncated)}
              history={derivationHistory}
              onSelectDocument={onSelectSource}
            />
          )}

          {loading && <p className="flex items-center gap-2 text-xs text-muted-foreground"><LoaderCircle className="h-3.5 w-3.5 animate-spin" />{copy.detail.loading}</p>}
          {!loading && detail && detail.status !== 'ready' && <p className="text-xs text-muted-foreground">{copy.detail.additional_unavailable}</p>}
          {derived && canInvalidate && <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs leading-5 text-muted-foreground">{copy.detail.derived_notice}</p>}

          {primaryDate && <p className="flex items-center gap-2 border-t border-border pt-5 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" />{formatMemoryDate(primaryDate)}</p>}
        </div>
      )}

      {/* Invalidar é destrutivo mas não é a ação principal: fica no rodapé em
          tom neutro e só assume o vermelho no hover. */}
      {canInvalidate && mode !== 'edit' && (
        <footer className="flex justify-start border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setMode('delete')}
            className="gap-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <CircleX className="size-3.5" />
            {copy.detail.delete}
          </Button>
        </footer>
      )}

      <AlertDialog open={mode === 'delete'} onOpenChange={(open) => { if (!open && !processing) setMode('view') }}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.detail.delete_title}</AlertDialogTitle>
            <AlertDialogDescription>{copy.detail.delete_description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>{copy.detail.cancel}</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} disabled={processing} onClick={(event) => { event.preventDefault(); remove() }}>
              {processing && <LoaderCircle className="animate-spin" />}{processing ? copy.detail.deleting : copy.detail.delete_confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  )
}

function DocumentLink({ label, title, description, onSelect }: { label: string; title: string; description: string; onSelect: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onSelect} className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground"><FileText className="h-3.5 w-3.5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium text-foreground">{title}</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">{description}</span>
          </span>
          <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6}>{label}</TooltipContent>
    </Tooltip>
  )
}
